import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { Inject, Injectable } from '@angular/core';
import {
    AWS_AMPLIFY,
    AWS_AMPLIFY_AUTH,
    ISettings,
    SETTINGS
} from '@lsq-frontend/angular-shared/settings';
import {
    CreateTotpMfaTokenRequest,
    VerifyTotpMfaTokenRequest
} from '@lsq-frontend/remote-services/enterprise/common';
import Amplify, { Auth } from 'aws-amplify';
import { IAuthenticationResult, MFAType } from '../../models/common';
import {
    GenericApiServiceFactory,
    GenericBoundedApiService
} from '@lsq-frontend/angular-shared/api-service';
import { ApiRoutes } from '@lsq-frontend/angular-shared/app-constants';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    public user = null;
    private _createTotpMfaTokenService: GenericBoundedApiService;
    private _verifyTotpMfaTokenService: GenericBoundedApiService;

    constructor(
        @Inject(SETTINGS) private _settings: ISettings,
        @Inject(AWS_AMPLIFY) private _amplify: typeof Amplify,
        @Inject(AWS_AMPLIFY_AUTH) private _amplifyAuth: typeof Auth,
        private _genericApiServiceFactory: GenericApiServiceFactory
    ) {
        SingletonServiceGuard.register('AuthenticationService');

        this._amplify.configure(this._cognitoConfigure(this._settings));

        this._createTotpMfaTokenService = this._genericApiServiceFactory.createBounded(
            `/${ApiRoutes.Api}/${ApiRoutes.CreateTotpMfaToken}`
        );

        this._verifyTotpMfaTokenService = this._genericApiServiceFactory.createBounded(
            `/${ApiRoutes.Api}/${ApiRoutes.VerifyTotpMfaToken}`
        );
    }

    public isOn(): boolean {
        return !!this._settings.cognitoUserPoolId && !!this._settings.cognitoClientId;
    }

    public async authenticateUserWithBackupCode(
        email: string,
        backupCode: string
    ): Promise<IAuthenticationResult> {
        try {
            const result = await this._amplifyAuth.signIn(email.toLowerCase());

            if (result.challengeName === 'CUSTOM_CHALLENGE') {
                try {
                    const user = await this._amplifyAuth.sendCustomChallengeAnswer(
                        result,
                        backupCode
                    );
                    this.user = user;

                    if (result.getSignInUserSession() !== null) {
                        return {
                            authorized: true,
                            emailAddress: email.toLowerCase(),
                            accessToken: result
                                .getSignInUserSession()
                                ?.getAccessToken()
                                ?.getJwtToken()
                        };
                    } else {
                        return {
                            authorized: false,
                            requiresAuthenticationCode: true,
                            emailAddress: email.toLowerCase(),
                            // Get last 4 digits of phone number passed from custom auth result
                            phoneNumberLast4: result['challengeParam'][
                                'CODE_DELIVERY_DESTINATION'
                            ].substr(8)
                        };
                    }
                } catch (e) {
                    return {
                        authorized: false,
                        emailAddress: email.toLowerCase(),
                        message: e.message
                    };
                }
            }
        } catch (e) {
            return {
                authorized: false,
                emailAddress: email.toLowerCase(),
                message: e.message
            };
        }
    }

    public async authenticateUserWithEmailAndPassword(
        emailAddress: string,
        password: string
    ): Promise<IAuthenticationResult> {
        try {
            const result = await this._amplifyAuth.signIn(emailAddress.toLowerCase(), password);

            if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
                this.clearSession();
                result.requiresPasswordReset = true;
                return result;
            }

            this.user = result;

            if (result.signInUserSession !== null) {
                return {
                    authorized: true,
                    emailAddress: emailAddress.toLowerCase(),
                    accessToken: result.getSignInUserSession()?.getAccessToken()?.getJwtToken()
                };
            } else if (result.challengeName === 'SMS_MFA') {
                return {
                    authorized: false,
                    requiresAuthenticationCode: true,
                    emailAddress: emailAddress.toLowerCase(),
                    phoneNumberLast4: result['challengeParam']['CODE_DELIVERY_DESTINATION'].substr(
                        8
                    ),
                    mfaType: MFAType.SMS
                };
            } else if (result.challengeName === 'SOFTWARE_TOKEN_MFA') {
                return {
                    authorized: false,
                    requiresAuthenticationCode: true,
                    emailAddress: emailAddress.toLowerCase(),
                    mfaType: MFAType.TOTP
                };
            }
        } catch (error) {
            return {
                authorized: false,
                emailAddress: emailAddress.toLowerCase(),
                message: error.message
            };
        }
    }

    public async resetForgottenPassword(
        emailAddress: string,
        verificationCode: string,
        newPassword: string
    ): Promise<IAuthenticationResult> {
        try {
            await this._amplifyAuth.forgotPasswordSubmit(
                emailAddress.toLowerCase(),
                verificationCode,
                newPassword
            );
            return { authorized: true };
        } catch (error) {
            return { authorized: false, message: error.message };
        }
    }

    public async resetPasswordForForcedReset(
        emailAddress: string,
        oldPassword: string,
        newPassword: string
    ): Promise<IAuthenticationResult> {
        try {
            const user = await this._amplifyAuth.signIn(emailAddress.toLowerCase(), oldPassword);
            const result = await this._amplifyAuth.completeNewPassword(user, newPassword);
            this.user = user;
            if (result.challengeName === 'SMS_MFA') {
                return {
                    authorized: false,
                    requiresAuthenticationCode: true,
                    emailAddress: emailAddress.toLowerCase(),
                    phoneNumberLast4: result['challengeParam']['CODE_DELIVERY_DESTINATION'].substr(
                        8
                    )
                };
            }

            return {
                authorized: true,
                accessToken: user.signInUserSession?.accessToken?.jwtToken
            };
        } catch (error) {
            return { authorized: false, message: error.message };
        }
    }

    public async resetPasswordUserInitiated(
        emailAddress: string,
        oldPassword: string,
        newPassword: string
    ): Promise<IAuthenticationResult> {
        try {
            const user = await this._amplifyAuth.currentAuthenticatedUser();
            await this._amplifyAuth.changePassword(user, oldPassword, newPassword);
            return { authorized: true };
        } catch (error) {
            return { authorized: false, message: error.message };
        }
    }

    public async sendForgotPasswordEmail(emailAddress: string): Promise<void> {
        if (this.isOn()) {
            await this._amplifyAuth.forgotPassword(emailAddress.toLowerCase());
        }
    }

    public async clearSession(): Promise<void> {
        try {
            await this._amplifyAuth.signOut();
            this.user = null;
        } catch (_) {
            //Nothing to do
        }
    }

    public async validateAuthenticationCode(
        authenticationCode: string,
        mfaType: MFAType
    ): Promise<IAuthenticationResult> {
        try {
            const challengeName = mfaType == MFAType.TOTP ? 'SOFTWARE_TOKEN_MFA' : 'SMS_MFA';
            const result = await this._amplifyAuth.confirmSignIn(
                this.user,
                authenticationCode,
                challengeName
            );
            this.user = result;

            var currentAuthenticatedUser = await this._amplifyAuth.currentAuthenticatedUser();

            try {
                if (currentAuthenticatedUser.attributes.email.endsWith('@lsq.com')) {
                    this._amplifyAuth.rememberDevice();
                }
            } catch (e) {
                console.warn(e);
            }

            return {
                authorized: true,
                accessToken: result.signInUserSession?.accessToken?.jwtToken,
                idToken: result.signInUserSession?.idToken?.jwtToken,
                refreshToken: result.signInUserSession?.refreshToken?.token,
                expiresInMillis: result.signInUserSession?.accessToken?.payload?.exp * 1000,
                phoneNumberLast4:
                    challengeName == 'SMS_MFA'
                        ? result['challengeParam']['CODE_DELIVERY_DESTINATION'].substr(8)
                        : ''
            };
        } catch (error) {
            return {
                authorized: false,
                message: error.message
            };
        }
    }

    private _cognitoConfigure(setting: ISettings) {
        this._amplify.configure({
            Auth: {
                identityPoolId: setting.cognitoUserPoolId,
                userPoolId: setting.cognitoUserPoolId,
                mandatorySignIn: false,
                userPoolWebClientId: setting.cognitoClientId
            }
        });
    }

    public async createTotpMfaToken(request: CreateTotpMfaTokenRequest) {
        const result = this._createTotpMfaTokenService.executeOnce(request);
        return result;
    }

    public async verifyTotpMfaToken(request: VerifyTotpMfaTokenRequest) {
        const result = this._verifyTotpMfaTokenService.executeOnce(request);
        return result;
    }

    public async getUserPreferredMfa(): Promise<string> {
        const user = await this._amplifyAuth.currentAuthenticatedUser();
        return user.preferredMFA;
    }

    public async setUserPreferredMfa(mfaPreference: any): Promise<void> {
        const user = await this._amplifyAuth.currentAuthenticatedUser();
        await this._amplifyAuth.setPreferredMFA(user, mfaPreference);
    }
}
